import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Image,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Package, Pencil, Plus, Trash, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import {
  getGetProductQueryKey,
  getListProductsQueryKey,
  useCreateProduct,
  useDeleteProduct,
  useGetBrand,
  useGetProduct,
  useListProducts,
  useReplaceProduct,
} from "../../api/generated/endpoints/teams/teams";
import type { ListProductsParams, Product, ProductOrder } from "../../api/generated/model";
import { freshUrls, soonestExpiry } from "../../api/signed-urls";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { CreatorFilter } from "../../platform/media/CreatorFilter";
import { useMembership } from "../../platform/membership";
import { managesBrands } from "../../platform/permissions";
import { readPref, writePref } from "../../platform/prefs";
import { CentredPage, type BackLink } from "../../ui/CentredPage";
import { ColumnsSetting } from "../../ui/ColumnsSetting";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { EmptyState } from "../../ui/EmptyState";
import { MasonryGrid } from "../../ui/MasonryGrid";
import { PageColumns } from "../../ui/PageColumns";
import { SearchToolbar } from "../../ui/SearchToolbar";
import { SortMenu, type SortGroup } from "../../ui/SortMenu";
import { useThumbFirst } from "../../ui/thumb-first";
import formClasses from "./forms.module.css";
import { ProductCard } from "./ProductCard";
import { ProductPreview } from "./ProductPreview";
import { productDraft } from "./product-draft";
import { ProductForm } from "./ProductForm";
import { TAG_LABEL } from "./TagSelect";
import { useRefetchOnRefusal } from "../../platform/refusal";

/** Live's products sort (`products-list/components/toolbar`): its three groups, in its words. */
const PRODUCT_ORDERS: readonly SortGroup<ProductOrder>[] = [
  {
    label: "Created Date",
    options: [
      { value: "newest", label: "Recently Added" },
      { value: "oldest", label: "Oldest First" },
    ],
  },
  {
    label: "Product Name",
    options: [
      { value: "a_to_z", label: "A-Z" },
      { value: "z_to_a", label: "Z-A" },
    ],
  },
  { label: "Popularity", options: [{ value: "most_popular", label: "Most Popular" }] },
];
/** Live's first order, "Recently Added"; the URL leaves it out. */
const FIRST_ORDER: ProductOrder = "newest";

/**
 * The brand's products as the live gallery (design-system.md 3.6): a masonry of product cards at
 * the persisted column count, live's creator chips and sort (spec A3d, kept in `?by=` and
 * `?order=`), "Add Product" for admins, and Edit and Delete in a card's hover pill (A3:
 * `manage_brands`). A card opens live's product preview in the shared lightbox (issue 828,
 * bug 25), stepping through the products shown.
 */
export function ProductListScreen() {
  const { teamId, brandId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/products",
  });
  // Another brand keeps the route's component mounted: the list starts anew, so no card,
  // preview or dialog of the last brand's stays under the next. An order or creators keep it.
  return <ProductList key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />;
}

/** One brand's products: its cards, kept while another order or creators load (`keepPreviousData`). */
function ProductList({ teamId, brandId }: { teamId: string; brandId: string }) {
  const search = useSearch({ from: "/signed-in/teams/$teamId/brands/$brandId/products" });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/products" });
  const me = useMembership(teamId);
  const order = search.order ?? FIRST_ORDER;
  const creators = search.by ?? [];
  const listed: ListProductsParams = {
    order,
    ...(creators.length === 0 ? {} : { created_by: creators }),
  };
  // The covers' signed URLs expire: the list is read again before they do (#123). Another order
  // or creator keeps the cards shown until its own list is read.
  const products = useListProducts(teamId, brandId, listed, {
    query: {
      ...freshUrls((list: Product[]) => soonestExpiry(list.map((each) => each.urls_expire_at))),
      placeholderData: keepPreviousData,
    },
  });
  const [columns, setColumns] = useState(() => readPref("productColumns"));
  const [deleting, setDeleting] = useState<Product | undefined>(undefined);
  // The product the preview shows, by id; null while it is closed.
  const [previewing, setPreviewing] = useState<string | null>(null);
  const params = { teamId, brandId };
  const canManage = me !== undefined && managesBrands(me.role);
  // Live's search; the list route takes none, so it filters the names read, in the list's order.
  const query = (search.search ?? "").toLowerCase();
  const shown = (products.data ?? []).filter((product) =>
    product.name.toLowerCase().includes(query),
  );
  const filtered = query !== "" || creators.length > 0;
  const toolbar = (
    <SearchToolbar
      search={{
        label: "Search by product name",
        value: search.search ?? "",
        delay: 0,
        onChange: (value) =>
          void navigate({
            search: (prev) => ({ ...prev, search: value || undefined }),
            replace: true,
          }),
      }}
      left={
        <CreatorFilter
          teamId={teamId}
          value={creators}
          onChange={(by) =>
            void navigate({
              search: (prev) => ({ ...prev, by: by.length === 0 ? undefined : by }),
              replace: true,
            })
          }
        />
      }
      right={
        <Group gap="sm" wrap="nowrap">
          <SortMenu
            options={PRODUCT_ORDERS}
            value={order}
            onChange={(next) =>
              void navigate({
                search: (prev) => ({ ...prev, order: next === FIRST_ORDER ? undefined : next }),
                replace: true,
              })
            }
          />
          <ColumnsSetting
            value={columns}
            onChange={(next) => {
              setColumns(next);
              writePref("productColumns", next);
            }}
          />
          {canManage ? (
            <Button
              variant="outline"
              leftSection={<Plus size={20} />}
              renderRoot={(props) => (
                <Link {...props} to="/teams/$teamId/brands/$brandId/products/new" params={params} />
              )}
            >
              Add Product
            </Button>
          ) : null}
        </Group>
      }
    />
  );
  return (
    <PageColumns top={toolbar}>
      {products.isPending ? <Loader size="sm" aria-label="Loading products" /> : null}
      {products.isError ? <ErrorAlert error={products.error} /> : null}
      {products.isSuccess && shown.length === 0 && !filtered ? (
        <EmptyState
          image={<Package size={48} strokeWidth={1.5} />}
          title="No products yet"
          description="A product's pictures and copy guide what the studio makes for it."
        />
      ) : null}
      {products.isSuccess && shown.length === 0 && filtered ? (
        // Live's words for a search or creators that match nothing.
        <EmptyState
          image={<Package size={48} strokeWidth={1.5} />}
          title="No results found"
          description="Check for typos or filters, or try a different search query."
        />
      ) : null}
      <MasonryGrid columns={columns} gap={0} label="Products">
        {shown.map((product) => {
          const ids = { ...params, productId: product.id };
          const open = (props: Record<string, unknown>) => (
            <Link {...props} to="/teams/$teamId/brands/$brandId/products/$productId" params={ids} />
          );
          return (
            <ProductCard
              key={product.id}
              product={product}
              onOpen={() => setPreviewing(product.id)}
              {...(canManage
                ? {
                    menu: (
                      <ProductActions
                        name={product.name}
                        edit={open}
                        onDelete={() => setDeleting(product)}
                      />
                    ),
                  }
                : {})}
            />
          );
        })}
      </MasonryGrid>
      <ProductPreview
        teamId={teamId}
        brandId={brandId}
        products={shown}
        current={previewing}
        onShow={setPreviewing}
        canManage={canManage}
        onDelete={setDeleting}
        paused={deleting !== undefined}
      />
      {deleting === undefined ? null : (
        <DeleteProduct
          teamId={teamId}
          brandId={brandId}
          product={deleting}
          onCancel={() => setDeleting(undefined)}
          onDeleted={() => toProducts(navigate, teamId, brandId)}
        />
      )}
    </PageColumns>
  );
}

interface ProductActionsProps {
  name: string;
  edit: (props: Record<string, unknown>) => ReactNode;
  onDelete: () => void;
}

/** Edit and Delete, white on the card's frosted pill. */
function ProductActions({ name, edit, onDelete }: ProductActionsProps) {
  return (
    <>
      <Tooltip label="Edit">
        <ActionIcon variant="text-white" size={28} aria-label={`Edit ${name}`} renderRoot={edit}>
          <Pencil size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Delete">
        <ActionIcon variant="text-white" size={28} aria-label={`Delete ${name}`} onClick={onDelete}>
          <Trash2 size={16} />
        </ActionIcon>
      </Tooltip>
    </>
  );
}

/** The brand the product belongs to, in a pill beside the title, as live shows it. */
function BrandBadge({ teamId, brandId }: { teamId: string; brandId: string }) {
  const name = useGetBrand(teamId, brandId, { query: { select: (brand) => brand.name } }).data;
  if (name === undefined) return null;
  return (
    <span className={formClasses.badge} title={name}>
      {name}
    </span>
  );
}

/** The back arrow of a product's centered page: to the brand's products. */
function backTo(teamId: string, brandId: string) {
  return (props: Record<string, unknown>) => (
    <Link {...props} to="/teams/$teamId/brands/$brandId/products" params={{ teamId, brandId }} />
  );
}

/** The brand's products after a delete: an edit left in its form has nothing left to save. */
function toProducts(navigate: ReturnType<typeof useNavigate>, teamId: string, brandId: string) {
  return navigate({
    to: "/teams/$teamId/brands/$brandId/products",
    params: { teamId, brandId },
    replace: true,
    ignoreBlocker: true,
  });
}

/** A new product on its own route: a saved product opens its page. */
export function NewProductScreen() {
  const { teamId, brandId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/products/new",
  });
  const navigate = useNavigate();
  return (
    <NewProduct
      teamId={teamId}
      brandId={brandId}
      back={backTo(teamId, brandId)}
      onCreated={(productId) =>
        // Saved: the form's leave prompt has nothing to ask.
        navigate({
          to: "/teams/$teamId/brands/$brandId/products/$productId",
          params: { teamId, brandId, productId },
          ignoreBlocker: true,
        })
      }
    />
  );
}

export interface NewProductProps {
  teamId: string;
  brandId: string;
  /** The back arrow's link. */
  back: BackLink;
  /** Where the page goes with the product saved; it passes the form's leave prompt. */
  onCreated: (productId: string) => Promise<void>;
}

/**
 * A new product, wherever it is drawn (Classic's route, or Pro's, spec
 * ui-perf/ui-switch-classic-simple-pro PR e4).
 */
export function NewProduct({ teamId, brandId, back, onCreated }: NewProductProps) {
  const queryClient = useQueryClient();
  const onError = useRefetchOnRefusal(getListProductsQueryKey(teamId, brandId));
  const create = useCreateProduct({
    mutation: {
      onError,
      onSuccess: async (product) => {
        await queryClient.invalidateQueries({ queryKey: getListProductsQueryKey(teamId, brandId) });
        await onCreated(product.id);
      },
    },
  });
  return (
    <CentredPage
      title="Add Product"
      back={back}
      titleRight={<BrandBadge teamId={teamId} brandId={brandId} />}
    >
      <ProductForm
        initial={productDraft()}
        library={{ teamId, brandId }}
        editing={false}
        pending={create.isPending}
        error={create.error}
        onSubmit={(data) => create.mutate({ teamId, brandId, data })}
      />
    </CentredPage>
  );
}

/** A product on its own route: back, and a delete, open the brand's products. */
export function ProductScreen() {
  const { teamId, brandId, productId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/products/$productId",
  });
  const navigate = useNavigate();
  return (
    <ProductPage
      teamId={teamId}
      brandId={brandId}
      productId={productId}
      back={backTo(teamId, brandId)}
      onDeleted={() => toProducts(navigate, teamId, brandId)}
    />
  );
}

export interface ProductPageProps {
  teamId: string;
  brandId: string;
  productId: string;
  /** The back arrow's link. */
  back: BackLink;
  /** Where the page goes with the product deleted; it passes the form's leave prompt. */
  onDeleted: () => Promise<void>;
}

/**
 * A product: the whole form for admins, a read-only view for everyone else, wherever it is drawn
 * (Classic's route, or Pro's, spec ui-perf/ui-switch-classic-simple-pro PR e4).
 */
export function ProductPage({ teamId, brandId, productId, back, onDeleted }: ProductPageProps) {
  const me = useMembership(teamId);
  const product = useGetProduct(teamId, brandId, productId, {
    query: freshUrls((read: Product) => read.urls_expire_at),
  });
  if (product.isPending) return <Loader size="sm" aria-label="Loading the product" />;
  if (product.isError) return <ErrorAlert error={product.error} />;
  if (me === undefined || !managesBrands(me.role)) {
    return <ProductView product={product.data} back={back} />;
  }
  return (
    <EditProduct
      teamId={teamId}
      brandId={brandId}
      product={product.data}
      back={back}
      onDeleted={onDeleted}
    />
  );
}

interface EditProductProps {
  teamId: string;
  brandId: string;
  product: Product;
  back: BackLink;
  onDeleted: () => Promise<void>;
}

function EditProduct({ teamId, brandId, product, back, onDeleted }: EditProductProps) {
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const ids = { teamId, brandId, productId: product.id };
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey(teamId, brandId) }),
      queryClient.invalidateQueries({
        queryKey: getGetProductQueryKey(teamId, brandId, product.id),
      }),
    ]);
  const onError = useRefetchOnRefusal(
    getListProductsQueryKey(teamId, brandId),
    getGetProductQueryKey(teamId, brandId, product.id),
  );
  const replace = useReplaceProduct({ mutation: { onSuccess: refresh, onError } });
  return (
    <CentredPage
      title={product.name}
      back={back}
      titleRight={<BrandBadge teamId={teamId} brandId={brandId} />}
      right={
        <Tooltip label="Delete">
          <ActionIcon
            variant="text"
            size={32}
            aria-label="Delete product"
            onClick={() => setDeleting(true)}
          >
            <Trash size={16} strokeWidth={1.5} />
          </ActionIcon>
        </Tooltip>
      }
    >
      <Stack gap="lg" pb="md">
        <ProductForm
          // A saved product restarts the form from the server's answer.
          key={product.updated_at}
          initial={productDraft(product)}
          library={{ teamId, brandId }}
          editing
          pending={replace.isPending}
          error={replace.error}
          onSubmit={(data) => replace.mutate({ ...ids, data })}
        />
        {replace.isSuccess ? (
          <Text component="output" size="sm">
            Saved.
          </Text>
        ) : null}
        <Stack gap="xs">
          {/* Live's `preprocessingDescription`, which every generation reads (spec A3e): ours shows
              it, live's form does not. */}
          <Title order={2} size="h4">
            What the AI reads
          </Title>
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {product.ai_description ?? "Not generated yet"}
          </Text>
        </Stack>
      </Stack>
      {deleting ? (
        <DeleteProduct
          teamId={teamId}
          brandId={brandId}
          product={product}
          onCancel={() => setDeleting(false)}
          onDeleted={onDeleted}
        />
      ) : null}
    </CentredPage>
  );
}

interface DeleteProductProps {
  teamId: string;
  brandId: string;
  product: Product;
  onCancel: () => void;
  onDeleted: () => Promise<void>;
}

/** Asks, then deletes the product and calls `onDeleted`: the brand's products (A3). */
function DeleteProduct({ teamId, brandId, product, onCancel, onDeleted }: DeleteProductProps) {
  const queryClient = useQueryClient();
  const ids = { teamId, brandId, productId: product.id };
  const onError = useRefetchOnRefusal(getListProductsQueryKey(teamId, brandId));
  const remove = useDeleteProduct({
    mutation: {
      onError,
      onSuccess: async () => {
        queryClient.removeQueries({ queryKey: getGetProductQueryKey(teamId, brandId, product.id) });
        await queryClient.invalidateQueries({ queryKey: getListProductsQueryKey(teamId, brandId) });
        onCancel();
        await onDeleted();
      },
    },
  });
  return (
    <ConfirmModal
      opened
      title={`Delete ${product.name}?`}
      confirmLabel="Delete product"
      onConfirm={() => remove.mutate(ids)}
      onCancel={onCancel}
      pending={remove.isPending}
      error={remove.isError ? <ErrorAlert error={remove.error} /> : null}
    >
      <Text size="sm">Its images are released, and nobody in the team can open it again.</Text>
    </ConfirmModal>
  );
}

/** A read-only page's picture, a quarter of the page wide: its thumbnail, the file when it has
 *  none or the thumbnail fails (spec ui-perf/product-images). */
function ViewImage({ thumb, full, alt }: { thumb: string | null | undefined; full: string; alt: string }) {
  const picture = useThumbFirst(thumb, full);
  return <Image src={picture.src} alt={alt} radius="md" fit="cover" loading="lazy" onError={picture.onError} />;
}

function ProductView({ product, back }: { product: Product; back: BackLink }) {
  const rows: [string, string | null][] = [
    ["Tagline", product.tagline],
    ["Description", product.description],
    ["Price", product.price],
    ["Offer", product.offer],
    ["Key benefits", product.key_benefits.join("\n") || null],
    ["Use cases", product.use_cases.join("\n") || null],
    ["What the AI reads", product.ai_description ?? "Not generated yet"],
  ];
  return (
    <CentredPage title={product.name} back={back}>
      <Stack gap="lg" pb="md">
        <Table>
          <Table.Tbody>
            {rows.map(([label, value]) => (
              <Table.Tr key={label}>
                <Table.Th w={180} scope="row" style={{ verticalAlign: "top" }}>
                  {label}
                </Table.Th>
                <Table.Td style={{ whiteSpace: "pre-wrap" }}>{value ?? "Not set"}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Stack gap="xs">
          <Title order={2} size="h4">
            Images
          </Title>
          {product.images.length === 0 ? <Text size="sm">No images yet.</Text> : null}
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            {product.images.map((image) => (
              <Stack key={image.asset_id} gap={4}>
                {image.url === null ? null : (
                  <ViewImage thumb={image.thumb_url} full={image.url} alt={TAG_LABEL[image.tag]} />
                )}
                <Group gap="xs">
                  <Badge variant="light">{TAG_LABEL[image.tag]}</Badge>
                  {image.is_main === true ? <Badge>Main</Badge> : null}
                </Group>
              </Stack>
            ))}
          </SimpleGrid>
        </Stack>
      </Stack>
    </CentredPage>
  );
}
